import { useCallback, useEffect, useState } from 'react'
import { IconAlertTriangle, IconCheck, IconSearch } from '@tabler/icons-react'
import { Alert, Button, Group, Modal, ScrollArea, Table, TextInput } from '@mantine/core'
import { FIELD_CLASSES, MODAL_CONTENT } from '@/components/ui/formModal.tokens'
import { useDebounce } from '@/hooks/useDebounce'
import { cn } from '@/utils/cn'
import { useClient, type OrpcClient } from '@/hooks/useClient'
import { DANGER_BTN, HINT, ID_CHIP, SECONDARY_BTN, TABLE_HEAD, TABLE_ROW } from './tokens'

type SearchResult = Awaited<ReturnType<OrpcClient['zocdocPractitionerSearch']>>
type StaffPractitioner = NonNullable<SearchResult['practitioners']>[number]
type LinkResult = Awaited<ReturnType<OrpcClient['zocdocLinkPractitioner']>>

/** What the popup is linking: the Zocdoc side, and whatever it is already pointed at. */
export type LinkTarget = {
  /**
   * The Zocdoc provider (`pr_…`). Chosen by the caller, because a group of link rows sharing one
   * NPI can hold more than one provider and this popup must never guess which is meant.
   */
  zocdocProviderId: string
  zocdocName?: string
  zocdocNpi?: string
  zocdocSpecialty?: string
  /** How many provider/location rows this link covers — all of them take the practitioner. */
  entityCount: number
  /** Set when this is a correction rather than a first link. */
  currentPractitionerId?: string
  currentPractitionerName?: string
}

const PAGE_SIZE = 20

const conflictMessage = (conflict: NonNullable<LinkResult['conflict']>) =>
  conflict.kind === 'practitioner-has-other-provider'
    ? `That practitioner is already linked to Zocdoc provider ${conflict.existing}. Linking them to this one as well would leave two Zocdoc providers pointing at one calendar, so nothing was changed.`
    : `This Zocdoc provider is already linked to practitioner ${conflict.existing}. Pointing it at a second practitioner would send its bookings to the wrong calendar, so nothing was changed.`

/**
 * Links a Zocdoc provider to a beHuman practitioner **by hand** — the operator says these are the
 * same person, and nothing has to match for them to say it.
 *
 * That is the point of the screen rather than a shortcoming of it: Zocdoc reports no NPI for some
 * providers, a different one for others, and spells names its own way, so an NPI match cannot be
 * the only road to a mapped link. What the popup owes in exchange is that the operator sees
 * exactly what they are overriding — Zocdoc's name and NPI beside the chosen practitioner's — and
 * that the two genuinely damaging cases stay refused by middleware: a provider already claimed by
 * someone else, and a practitioner already claiming another provider.
 *
 * Only beHuman's own doctors are offered. The NPPES referral directory and the care coordinators
 * are filtered out server-side, because a link to either passes every check and then never books
 * anything.
 */
export function LinkPractitionerModal({
  target,
  onClose,
  onLinked,
}: {
  target: LinkTarget | null
  onClose: () => void
  /** A link was written. The caller re-reads, so the row on screen matches what was saved. */
  onLinked: () => void
}) {
  const client = useClient()
  const [query, setQuery] = useState('')
  const debounced = useDebounce(query, 300)
  const [page, setPage] = useState(0)
  const [result, setResult] = useState<SearchResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [chosen, setChosen] = useState<StaffPractitioner | null>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // A fresh popup per provider: the previous one's search, page and choice would all be wrong here.
  useEffect(() => {
    setQuery('')
    setPage(0)
    setChosen(null)
    setFailure(null)
  }, [target?.zocdocProviderId])

  useEffect(() => {
    setPage(0)
  }, [debounced])

  const search = useCallback(async () => {
    setLoading(true)
    try {
      setResult(
        await client.zocdocPractitionerSearch({
          ...(debounced && { query: debounced }),
          offset: page * PAGE_SIZE,
          count: PAGE_SIZE,
        }),
      )
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not reach middleware')
    } finally {
      setLoading(false)
    }
  }, [client, debounced, page])

  useEffect(() => {
    if (target) void search()
  }, [target, search])

  /**
   * A correction is an unlink followed by a link, behind this one confirm: leaving it as two
   * separate acts is how a provider ends up with no practitioner at all when the operator stops
   * halfway.
   */
  const confirm = async () => {
    if (!target || !chosen) return
    setSaving(true)
    setFailure(null)
    try {
      if (target.currentPractitionerId && target.currentPractitionerId !== chosen.practitionerId) {
        // Takes the identifier off the previous practitioner so the link below is not refused as
        // a conflict. It cannot half-succeed any more: the identifier is the whole link.
        await client.zocdocUnlinkPractitioner({ zocdocProviderId: target.zocdocProviderId })
      }

      const linked = await client.zocdocLinkPractitioner({
        zocdocProviderId: target.zocdocProviderId,
        practitionerId: chosen.practitionerId,
      })
      if (linked.status === 'conflict' && linked.conflict) {
        setFailure(conflictMessage(linked.conflict))
        return
      }
      // Verified against Zocdoc before anything was written: the link would never have been able
      // to publish, so it is refused here rather than failing silently on every later slot change.
      if (linked.status === 'refused' && linked.refusal) {
        setFailure(linked.refusal.detail)
        return
      }

      onLinked()
      onClose()
    } catch (cause) {
      setFailure(cause instanceof Error ? cause.message : 'Could not link the practitioner')
    } finally {
      setSaving(false)
    }
  }

  const practitioners = result?.practitioners ?? []
  const total = result?.total ?? practitioners.length
  const npiMismatch = Boolean(target?.zocdocNpi && chosen?.npi && target.zocdocNpi !== chosen.npi)

  return (
    <Modal
      classNames={{
        content: MODAL_CONTENT,
        title: '!text-[17px] !font-extrabold !tracking-[-0.3px]',
      }}
      radius={18}
      size="52rem"
      opened={target !== null}
      onClose={onClose}
      title={target?.currentPractitionerId ? 'Change beHuman profile' : 'Link to beHuman profile'}
    >
      {target && (
        <div className="flex flex-col gap-3">
          {/* The Zocdoc side, stated up front: this is the evidence a manual link overrides, and
              it has to be readable without leaving the popup. */}
          <div className="rounded-xl bg-[#F8F9FB] p-3">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <span className="text-[13px] font-bold text-[#1F2733]">
                {target.zocdocName ?? 'Name unknown'}
              </span>
              <span className={HINT}>
                {target.zocdocSpecialty ? `${target.zocdocSpecialty} · ` : ''}
                {target.zocdocNpi ? `NPI ${target.zocdocNpi}` : 'no NPI reported'}
                {` · ${target.entityCount} ${target.entityCount === 1 ? 'location' : 'locations'}`}
              </span>
            </div>
            <div className={cn([HINT, 'mt-1'])}>
              Zocdoc provider <span className={ID_CHIP}>{target.zocdocProviderId}</span>
              {target.currentPractitionerId ? (
                <>
                  {' · currently '}
                  {target.currentPractitionerName ?? target.currentPractitionerId}
                </>
              ) : null}
            </div>
          </div>

          <TextInput
            classNames={FIELD_CLASSES}
            leftSection={<IconSearch size={16} />}
            label="beHuman practitioner"
            description="Search our own doctors by name, or by exact email address"
            placeholder="Name or email"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />

          {failure && (
            <Alert color="red" radius="md">
              {failure}
            </Alert>
          )}

          {result && !result.ok && (
            <Alert color="red" radius="md" title="Could not search practitioners">
              <span className="text-[13px]">{result.error}</span>
            </Alert>
          )}

          {result?.ok && practitioners.length === 0 && (
            <Alert color="blue" radius="md">
              {debounced
                ? `No beHuman doctor matches “${debounced}”.`
                : 'No beHuman doctors found. Only practitioners with a doctor role are offered here — care coordinators and the referral directory are not.'}
            </Alert>
          )}

          {practitioners.length > 0 && (
            <ScrollArea.Autosize mah={300} type="auto">
              <Table highlightOnHover verticalSpacing={6} fz="sm">
                <Table.Thead>
                  <Table.Tr className={TABLE_ROW}>
                    <Table.Th className={TABLE_HEAD}>Name</Table.Th>
                    <Table.Th className={cn([TABLE_HEAD, 'w-[130px]'])}>NPI</Table.Th>
                    <Table.Th className={cn([TABLE_HEAD, 'w-[170px]'])}>Organization</Table.Th>
                    <Table.Th className={cn([TABLE_HEAD, 'w-[150px]'])} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {practitioners.map((practitioner) => (
                    <Table.Tr
                      key={practitioner.practitionerId}
                      className={cn([
                        TABLE_ROW,
                        chosen?.practitionerId === practitioner.practitionerId && 'bg-[#F1F8F3]',
                      ])}
                    >
                      <Table.Td>
                        <div className="text-[13px] font-bold text-[#1F2733]">
                          {practitioner.name ?? practitioner.practitionerId}
                        </div>
                        {/* Shown per row, not only on confirm: hand-picking makes "this doctor
                            has no calendar" the likeliest mistake on the screen. */}
                        {!practitioner.hasSchedule && (
                          <span className="behuman-v2-mulish mt-0.5 inline-flex items-center rounded-md bg-[#FEF3C7] px-1.5 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#B45309] uppercase">
                            no schedule
                          </span>
                        )}
                      </Table.Td>
                      <Table.Td>
                        {practitioner.npi ? (
                          <span
                            className={cn([
                              'text-[12.5px]',
                              target.zocdocNpi && practitioner.npi !== target.zocdocNpi
                                ? 'font-bold text-[#C2410C]'
                                : 'text-[#1F2733]',
                            ])}
                          >
                            {practitioner.npi}
                          </span>
                        ) : (
                          <span className={HINT}>none</span>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <span className={HINT}>{practitioner.organizationId ?? '—'}</span>
                      </Table.Td>
                      <Table.Td>
                        {practitioner.zocdocProviderId === target.zocdocProviderId ? (
                          <span className="behuman-v2-mulish inline-flex items-center gap-1 rounded-md bg-[#DCFCE7] px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#15803D] uppercase">
                            <IconCheck size={12} />
                            linked
                          </span>
                        ) : practitioner.zocdocProviderId ? (
                          // Shown, never offered: the write would be refused anyway.
                          <span
                            className="behuman-v2-mulish inline-flex items-center rounded-md bg-[#FEF3C7] px-2 py-0.5 text-[11px] font-bold tracking-[0.04em] text-[#B45309] uppercase"
                            title={`Linked to Zocdoc provider ${practitioner.zocdocProviderId}, not this one`}
                          >
                            other provider
                          </span>
                        ) : (
                          <Button
                            {...SECONDARY_BTN}
                            size="compact-xs"
                            onClick={() => setChosen(practitioner)}
                          >
                            {chosen?.practitionerId === practitioner.practitionerId
                              ? 'Selected'
                              : 'Select'}
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea.Autosize>
          )}

          {total > PAGE_SIZE && (
            <Group gap={6} justify="space-between">
              <span className={HINT}>
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </span>
              <Group gap={6}>
                <Button
                  {...SECONDARY_BTN}
                  size="compact-xs"
                  disabled={page === 0 || loading}
                  onClick={() => setPage((current) => current - 1)}
                >
                  Previous
                </Button>
                <Button
                  {...SECONDARY_BTN}
                  size="compact-xs"
                  disabled={(page + 1) * PAGE_SIZE >= total || loading}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </Group>
            </Group>
          )}

          {chosen && (
            <div className="flex flex-col gap-2 border-t border-[#ECEEF1] pt-3">
              {/* Said plainly rather than blocked: an NPI mismatch is exactly the case manual
                  linking exists for, and exactly the case where it is worth being sure. */}
              {npiMismatch && (
                <Alert
                  color="orange"
                  radius="md"
                  icon={<IconAlertTriangle size={16} />}
                  title="The NPIs do not match"
                >
                  <span className="text-[13px]">
                    Zocdoc reports NPI {target.zocdocNpi} for this provider, while{' '}
                    {chosen.name ?? chosen.practitionerId} carries {chosen.npi}. Link them only if
                    you know they are the same person.
                  </span>
                </Alert>
              )}

              {!chosen.hasSchedule && (
                <Alert
                  color="orange"
                  radius="md"
                  icon={<IconAlertTriangle size={16} />}
                  title="This practitioner has no schedule"
                >
                  <span className="text-[13px]">
                    Zocdoc bookings for this provider will have no calendar to land on until one
                    exists. The link can be made now, but it will not book anything yet.
                  </span>
                </Alert>
              )}

              <Group justify="flex-end" gap={8}>
                <Button {...SECONDARY_BTN} onClick={() => setChosen(null)} disabled={saving}>
                  Clear selection
                </Button>
                <Button
                  {...(npiMismatch ? DANGER_BTN : SECONDARY_BTN)}
                  loading={saving}
                  onClick={() => void confirm()}
                >
                  {target.currentPractitionerId
                    ? `Move to ${chosen.name ?? chosen.practitionerId}`
                    : `Link ${chosen.name ?? chosen.practitionerId}`}
                </Button>
              </Group>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}
